import ValidateModule from "./ValidateModule";

import type { IEventChannel } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type { GeoMap } from "../model/GeoMap";
import type {ApiOpts} from "@esWebAPI/es/model/Models";
import type CacheModule from "./CacheModule";

const es = global.es;

/**
 * @class GeoMapTemplateModule
 */
export default class GeoMapTemplateModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.get = this.get.bind(this);
        this.getByIds = this.getByIds.bind(this);
        this.create = this.create.bind(this);
        this.remove = this.remove.bind(this);
        this.listByGeoMap = this.listByGeoMap.bind(this);
        this.list = this.list.bind(this);
        this.listExplicitShared = this.listExplicitShared.bind(this);
        this.count = this.count.bind(this);
        this.countExplicitShared = this.countExplicitShared.bind(this);
        this.cloneFromTemplate = this.cloneFromTemplate.bind(this);
        this.isGeoMapInTemplate = this.isGeoMapInTemplate.bind(this);
    }

    /**
     * Get the resource from the server
     * 
     * @param id 
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    get(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }

    /**
     * Get the resource from the server
     * 
     * @param ids
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getByIds(ids: number[], opts?: ApiOpts) {
        if (!ValidateModule.checkIds(ids)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/ids?ids=" + es.util.url.stringify(ids);
        return this.cache.doGet(this.rqBroker, this.resourceName, url, ids, opts);
    }

    /**
     * POST a new resource to the server (note: id attrib must be -1)
     * 
     * @param resourceObj e.g. {"id":-1,"geoMapId":"geoMapId"}
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    create(resourceObj, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName),
            opts: opts,
            data: resourceObj
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
            this.eventChannel?.fireEvent("onCreateGeoMapTemplate", rs);
        });
    }

    /**
     * Removes the resource from the server
     * 
     * @param id
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    remove(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + id;
        return this.rqBroker.doDelete({url: url, opts: opts}).done((rs) => {
            this.cache?.clear(this.resourceName, id);
            this.eventChannel?.fireEvent("onRemoveGeoMapTemplate", id);
        });
    }

    /**
     * Build list query based on GeoMap
     * 
     * @param geoMap
     * TODO: UPDATE
     * 
     * @returns
     */
    listByGeoMap(geoMap: GeoMap) {
        let geoMapId;
        if (geoMap.$type.endsWith('TempGeoMap')) {
            geoMapId = geoMap.parentId;
        } else {
            geoMapId = geoMap.id;
        }

        const criteria = {
            '$type': 'Equal',
            'attr': 'geoMapId',
            'value': geoMapId
        };

        return this.list(criteria);
    }

    /**
     * Get the resource from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    list(criteria:any, sortOrder?:any, offset?: number, length?: number, opts?: ApiOpts) {
        return this.cache.doGetList(this.rqBroker, this.resourceName, criteria, sortOrder, offset, length, opts);
    }

    /**
     * Get the resource from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    listExplicitShared(criteria, sortOrder, offset: number, length: number, opts?: ApiOpts) {
        return this.cache.doGetList(this.rqBroker, this.resourceName + "/list_explicit_shared", criteria, sortOrder, offset, length, opts);
    }

    /**
     * Get a count of resources matching the given Criteria
     * 
     * @param criteria
     * @param opts
     * 
     * @returns long
     */
    count(criteria, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/count" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * Count geo map templates which are not owned by the current user and are explicitly shared with them
     * Count criteria
     * 
     * @param criteria
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    countExplicitShared(criteria, opts?: ApiOpts) {
        var url = es.api.getURL(this.resourceName) + "/count_explicit_shared" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * Create a GeoMap based on a GeoMapTemplate
     * 
     * @param id GeoMapTemplate id
     * @param names
     * @param workspaceId Workspace id
     * @param opts
     * 
     * @returns GeoMap
     */
    cloneFromTemplate(id: number, names?: {geoMapName: string, workspaceName?: string}, workspaceId?: number, opts?: ApiOpts) {
        let url = es.api.getURL(this.resourceName) + "/clone_from_template/" + id;
        if (names && names.geoMapName) {
            url += "?geoMapName=" + encodeURIComponent(names.geoMapName);

            if (names.workspaceName) {
                url += "&workspaceName=" + encodeURIComponent(names.workspaceName);
            }

            if (workspaceId) {
                url += "&workspaceId=" + workspaceId;
            }
        }

        return this.rqBroker.doPut({url: url, opts: opts});
    }

    /**
     * Checks if GeoMap (or parent if TempGeoMap) is used in a GeoMapTemplate
     * 
     * @param geoMapId
     * 
     * @returns a dfd.promise
     */
    isGeoMapInTemplate(geoMapId: number) {
        const url = es.api.getURL(this.resourceName) + "/is_in_template/" + geoMapId;
        return this.rqBroker.doGet({url: url});
    }
}
